import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getDatabase, ref as dbRef, get } from "firebase/database";
import Navbar from "../../components/navbar/Navbar";
import Footer from "../footer/Footer";
import { initMarkers, startSmoothScroll, stopSmoothScroll } from "../../lib/motion";
import "./blogpost.css";

/* Paragraphs come back from the admin panel as one long string. Split on blank
   lines first; fall back to single newlines so older posts still break up. */
const toParagraphs = (text) => {
  if (!text) return [];
  const normalised = String(text).replace(/\r\n?/g, "\n").trim();
  const byBlank = normalised.split(/\n{2,}/).filter(Boolean);
  if (byBlank.length > 1) return byBlank;
  return normalised.split(/\n+/).filter(Boolean);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const BlogPost = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  const [coverFailed, setCoverFailed] = useState(false);

  useEffect(() => {
    startSmoothScroll();
    return stopSmoothScroll;
  }, []);

  /* The marker only exists once the post has resolved. */
  useEffect(() => {
    if (status !== "ready") return undefined;
    return initMarkers();
  }, [status]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [postId]);

  useEffect(() => {
    if (!postId) {
      setStatus("missing");
      return undefined;
    }
    let cancelled = false;

    (async () => {
      try {
        const snapshot = await get(dbRef(getDatabase(), `blogs/${postId}`));
        if (cancelled) return;
        if (!snapshot.exists()) {
          setStatus("missing");
          return;
        }
        const data = snapshot.val();
        setPost({
          ...data,
          sections: data.sections || [
            { images: data.images || [], text: data.text || "" },
          ],
        });
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("unavailable");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [postId]);

  const shell = (children) => (
    <div className="post-page">
      <Navbar />
      <main className="post-main" id="main">
        {children}
      </main>
      <Footer />
    </div>
  );

  if (status === "loading") {
    return shell(
      <article className="post shell" aria-busy="true">
        <div className="skeleton post__sk-chip" />
        <div className="skeleton post__sk-title" />
        <div className="skeleton post__sk-media" />
        <div className="skeleton post__sk-para" />
        <div className="skeleton post__sk-para" />
      </article>
    );
  }

  if (status !== "ready") {
    const missing = status === "missing";
    return shell(
      <div className="post shell">
        <div className="post__state">
          <h1 className="t-h2">
            {missing ? "That story is not here" : "The story archive is unreachable"}
          </h1>
          <p className="t-body">
            {missing
              ? "The link may be out of date, or the story may have been taken down."
              : "We could not load this story. Reload the page to try again."}
          </p>
          <div className="post__state-actions">
            <Link className="btn-sales" to="/#blog">
              Back to all stories
            </Link>
            {!missing ? (
              <button
                type="button"
                className="btn-sales btn-sales--ghost"
                onClick={() => window.location.reload()}
              >
                Reload the page
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const title = post.title || post.heading || "Untitled story";
  const firstText = post.sections?.[0]?.text || "";
  const description = firstText
    ? `${String(firstText).replace(/<[^>]+>/g, "").slice(0, 155)}…`
    : "A story from SALES – Social Advancement for Livelihood and Educational Support.";
  const cover = post.sections?.[0]?.images?.[0];

  return shell(
    <>
      <Helmet>
        <title>{`${title} | SALES`}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${title} | SALES`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        {cover ? <meta property="og:image" content={cover} /> : null}
      </Helmet>

      <article className="post">
        <header className="post__header shell">
          <button
            type="button"
            className="post__back"
            onClick={() => navigate(-1)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M14 8H2m0 0 4.5-4.5M2 8l4.5 4.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>
          <p className="marker post__eyebrow">
            <span className="marker__num">SALES</span>
            <span>Field report</span>
          </p>
          <h1 className="t-display-2 post__title">{title}</h1>
          {post.date ? (
            <p className="post__meta">
              <time dateTime={String(post.date)}>{formatDate(post.date)}</time>
            </p>
          ) : null}
        </header>

        {cover && !coverFailed ? (
          <figure className="post__cover shell">
            <img
              src={cover}
              alt=""
              loading="eager"
              decoding="async"
              onError={() => setCoverFailed(true)}
            />
          </figure>
        ) : null}

        <div className="post__body shell">
          {post.sections.map((section, index) => {
            const images = Array.isArray(section.images) ? section.images : [];
            const rest = index === 0 ? images.slice(1) : images;
            return (
              <section className="post__section" key={index}>
                {toParagraphs(section.text).map((paragraph, i) => (
                  <p className="post__para" key={i}>
                    {paragraph}
                  </p>
                ))}
                {rest.length ? (
                  <div
                    className={`post__figures post__figures--${Math.min(
                      rest.length,
                      3
                    )}`}
                  >
                    {rest.map((image, i) => (
                      <img
                        key={`${image}-${i}`}
                        src={image}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ))}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>

        <div className="post__foot shell">
          <div className="post__foot-card">
            <div>
              <h2 className="t-h3">Work like this runs on donations</h2>
              <p className="t-body">
                Physiotherapy, medicine and school support are paid for one
                contribution at a time.
              </p>
            </div>
            <Link className="btn-sales btn-sales--donate" to="/#possibility">
              Donate
            </Link>
          </div>
          <Link className="link-arrow" to="/#blog">
            All stories
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M2 8h12m0 0-4.5-4.5M14 8l-4.5 4.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </article>
    </>
  );
};

export default BlogPost;
